import { createRef, ReactElement, useEffect, useRef, useState } from 'react';
// Design System
import { Font } from 'src/design-system/text/font';
import { Link } from '@utmb/design-system/link/link';
// Interfaces
import { CustomEventMenuDesktop } from '@utmb/types/events';
import { Slice } from '@utmb/types/slice-handler';
import { SliceInnerNavView } from '@utmb/types/index';
// Styles
import styles from './anchors-navigation.module.scss';
import variables from '@utmb/styles/common/common.module.scss';

export interface AnchorsNavigationProps extends SliceInnerNavView {
    itemSelected?: number;
}

export const AnchorsNavigation = (props: Slice<AnchorsNavigationProps>): ReactElement | null => {
    const { items, itemSelected, innerNavCta } = props;
    const [selected, setSelected] = useState(itemSelected || 0);
    const [isFreezed, setIsFreezed] = useState(false);

    const refElement = useRef<HTMLDivElement>(null);
    const refContainer = useRef<HTMLDivElement>(null);
    const refAnchors = useRef(Array(items.length).fill(createRef()));

    useEffect(() => {
        // Get the gutter width.
        const gutterWith: number = parseInt(getComputedStyle(document.body).getPropertyValue('--bs-gutter-x')) || 0;

        if (refContainer.current) {
            const offest = refAnchors.current[selected].offsetLeft - gutterWith;

            refContainer.current.scrollLeft = Math.max(0, offest);
        }
    }, [selected]);

    useEffect(() => {
        let observer: IntersectionObserver | null = null;
        const anchorElementToObserve = document.getElementById('anchors-navigation');
        const sectionsToObserve = items.map((item) => document.getElementById(item.uid));

        /**
         * When an observer is defined, unobserve the element and unset the var.
         * Like this, a new observer will be created with the new values
         */
        function removeObserver(): void {
            if (observer) {
                if (anchorElementToObserve) {
                    observer.unobserve(anchorElementToObserve);
                }

                if (sectionsToObserve.length) {
                    for (const section of sectionsToObserve) {
                        if (section) {
                            observer.unobserve(section);
                        }
                    }
                }

                observer = null;
            }
        }

        /**
         * Create an observer to know when the element is sticky
         */
        function createObserver(): void {
            // Remove the observer
            removeObserver();

            // Calculate the header and menu heigt
            const bodyComputedStyle = getComputedStyle(document.body);
            const headerBarHeight = (parseFloat(bodyComputedStyle.getPropertyValue('--top-bar-height')) || 0) * parseInt(variables.basePixels);
            const menuHeight = (parseFloat(bodyComputedStyle.getPropertyValue('--menu-sticky-height')) || 0) * parseInt(variables.basePixels);

            // Create a new observer
            observer = new IntersectionObserver(
                ([element]) => {
                    // Create a custom element and fire it
                    if (element.target === anchorElementToObserve) {
                        const event = new CustomEvent('inner-nav-sticky', {
                            detail: { isSticky: element.isIntersecting },
                        });

                        document.dispatchEvent(event);
                    } else if (element.isIntersecting && !isFreezed) {
                        const button = anchorElementToObserve?.querySelector(`[data-id="${element.target.id}"]`) as HTMLButtonElement;
                        const buttons = anchorElementToObserve?.querySelectorAll('button');

                        if (buttons?.length) {
                            const buttonIndex = [...buttons].indexOf(button);
                            setSelected(buttonIndex);
                        }
                    }
                },
                {
                    rootMargin: `0px 0px -${window.innerHeight - (headerBarHeight + menuHeight)}px 0px`,
                }
            );

            // Observe the element that will be sticky
            if (anchorElementToObserve) {
                observer.observe(anchorElementToObserve);
            }

            for (const section of sectionsToObserve) {
                if (section) {
                    observer.observe(section);
                }
            }
        }

        if (anchorElementToObserve) {
            createObserver();
            window.addEventListener('resize', createObserver);

            return () => {
                removeObserver();
                window.removeEventListener('resize', createObserver);
            };
        }
    }, [items, isFreezed]);

    useEffect(() => {
        function changeTopPosition(event: CustomEventMenuDesktop): void {
            refElement.current?.classList[event.detail.isHidden ? 'add' : 'remove'](styles.menu_is_hidden);
        }

        document.addEventListener('menu-sticky-hidden', changeTopPosition);

        return () => {
            document.removeEventListener('menu-sticky-hidden', changeTopPosition);
        };
    }, []);

    if (!items || !items.length) {
        return null;
    }

    const onClick = (key: number): void => {
        setSelected(key);
        setIsFreezed(true);

        // Calculate the anchor position and scroll to it.
        const anchorElement = document.getElementById(items[key].uid);

        if (anchorElement) {
            const topBarHeight = document.getElementById('top-bar')?.clientHeight || 0;
            const headerBarMenuHeight = document.querySelector('#header-bar-menu > nav')?.clientHeight || 0;
            const anchorNavigationHeight = document.getElementById('anchors-navigation')?.clientHeight || 0;
            const headerHeight = topBarHeight + headerBarMenuHeight + anchorNavigationHeight;

            const anchorTopPosition = anchorElement.getBoundingClientRect().top + document.documentElement.scrollTop - headerHeight;

            window.scrollTo({
                top: Math.max(0, anchorTopPosition),
                behavior: 'smooth',
            });
        }

        setTimeout(() => setIsFreezed(false), 2000);
    };

    return (
        <div id="anchors-navigation" ref={refElement} className={styles.sticky}>
            <div className={`${styles.container} container`} ref={refContainer}>
                {items.map((item, index) => (
                    <button
                        key={index}
                        ref={(el) => (refAnchors.current[index] = el)}
                        className={`${styles.link} ${selected === index ? styles.selected : ''}`}
                        onClick={() => onClick(index)}
                        data-id={item.uid}
                    >
                        <Font.FuturaBook as="span">{item.menuName}</Font.FuturaBook>
                    </button>
                ))}
                {innerNavCta && (
                    <Link.Button href={innerNavCta.slug} bgcolor="var(--utmb-color-default)" color="var(--utmb-color-white)" label={innerNavCta.label} />
                )}
            </div>
        </div>
    );
};
